/**
 * The player's options (spec 08 §3-4, 07 §4), kept in the browser between visits. Everything that
 * reads an option reads it from here: the menus change it through `SettingsStore.update`, and the
 * game, the picture and the sound follow through `subscribe`.
 *
 * Defaults are the faithful game (08 §5: "nothing the original did not show is shown by default").
 */

import { DEFAULT_KEYS, type KeyBindings } from '../input/keyboard';
import { sanitize } from './sanitize';

export type MusicMode = 'remastered' | '1987' | 'off';

export interface AudioSettings {
  /** The soundtrack's voice: the remaster (default), the exact 1987 SID, or none. */
  music: MusicMode;
  /** 0..1. */
  musicVolume: number;
  /** 0..1, apart from the music. */
  effectsVolume: number;
  /** Sounds the C64 did not have (blow, pop, chain, trap, death, level clear, HURRY UP): off by default (07 §4). */
  extraEffects: boolean;
}

export interface DisplaySettings {
  /** 'modern': the remake's look. '1987': the frame locked to the C64's sixteen colours, full dither, black cave. */
  look: 'modern' | '1987';
  /** Scanlines and a little more vignette. */
  crt: boolean;
  /** How hard the picture is pulled to the palette, 0..1 (the modern look; 1987 is always 1). */
  paletteLock: number;
  /** Screen pixels per texel: 1 is sharp (default), 2 and 3 the chunky pixel look. */
  pixelSize: 1 | 2 | 3;
  /** Colour-blind aid: angry monsters carry a pattern, a bubble about to burst shows it by shape, not only colour. */
  colourAid: boolean;
  /** Caps flashing: the clock's and pauses' pulses, lightning, bursts, the heart's pink flash (NIGHTDROP's flash cap). */
  reduceFlashing: boolean;
}

export interface PlaySettings {
  /** 1 is the game's own speed; below 1 is practice (0.5..1 in steps of 0.1), and the high score is not kept. */
  gameSpeed: number;
}

export interface Settings {
  audio: AudioSettings;
  display: DisplaySettings;
  play: PlaySettings;
  /** Player 1's and player 2's keys (KeyboardEvent.code values). */
  keys: readonly [KeyBindings, KeyBindings];
}

export const DEFAULT_SETTINGS: Settings = {
  audio: { music: 'remastered', musicVolume: 0.7, effectsVolume: 0.8, extraEffects: false },
  display: { look: 'modern', crt: false, paletteLock: 0.25, pixelSize: 1, colourAid: false, reduceFlashing: false },
  play: { gameSpeed: 1 },
  keys: DEFAULT_KEYS,
};

/** A change to some of the options: any section, any of its fields. */
export interface SettingsPatch {
  audio?: Partial<AudioSettings>;
  display?: Partial<DisplaySettings>;
  play?: Partial<PlaySettings>;
  keys?: readonly [KeyBindings, KeyBindings];
}

const STORAGE_KEY = 'bubble-bobble.settings.v1';

/** Where settings are kept; `localStorage` in the browser, a Map in tests. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage blocked (private window, site data off): options last the visit
  }
}

export class SettingsStore {
  private current: Settings;
  private readonly listeners = new Set<(s: Settings, before: Settings) => void>();

  constructor(private readonly storage: SettingsStorage | null = browserStorage()) {
    let raw: unknown = null;
    try {
      const text = storage?.getItem(STORAGE_KEY);
      raw = text ? JSON.parse(text) : null;
    } catch {
      raw = null;
    }
    this.current = sanitize(raw);
  }

  get(): Settings {
    return this.current;
  }

  /** Change some options; listeners hear of it and the result is saved. Values are clamped to what is allowed. */
  update(patch: SettingsPatch): void {
    const before = this.current;
    const s = this.current;
    this.current = sanitize({
      audio: { ...s.audio, ...patch.audio },
      display: { ...s.display, ...patch.display },
      play: { ...s.play, ...patch.play },
      keys: patch.keys ?? s.keys,
    });
    this.save();
    for (const fn of this.listeners) fn(this.current, before);
  }

  /** Back to the defaults: one section, or everything. */
  reset(section?: keyof Settings): void {
    if (section === undefined) return this.update(DEFAULT_SETTINGS);
    this.update({ [section]: DEFAULT_SETTINGS[section] });
  }

  /** Called with the new and the old settings after every change; returns the unsubscribe. */
  subscribe(fn: (s: Settings, before: Settings) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private save(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.current));
    } catch {
      // storage full or blocked: the options still hold for this visit
    }
  }
}
